import { Component, OnInit } from '@angular/core';
import { User } from '../../../models/User/user';
import { OperationError } from '../../../models/server-result/operation-error';
import { DocumentForecast } from '../../../models/dashboard/document-forecast';
import { VerticalBarChart } from '../../../models/charts/vertical-bar-chart';
import { ReportingApiService } from '../../../server-api/reporting-api/reporting-api.service';
import { ServerApiService } from '../../../server-api/server-api.service';
import { AppContextService } from '../../../services/app-context.service';
import { TranslateService } from '../../../services/translate/translate.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { DateLocalePipe } from '../../../services/date-locale.pipe';
import { OperationResult } from '../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmingContract } from '../../../models/contracts/confirming/confirming-contract';
import { ConfirmingContractsSearchCriteria } from '../../../models/contracts/confirming/confirming-contracts-search-criteria';
import { DocumentForecastByState } from '../../../models/dashboard/document-forecast-by-paymentstate';
import { Router } from '@angular/router';
import { FileIntegrationStateService } from '../../../services/file-integration-state.service';
import { SearchFilesIntegrationComponent } from '../../files-integration/search-files-integration/search-files-integration.component';
import { FileStatus } from '../../../models/enums/FileIntegration/integration-file-status';
import { CurrencyViewModel, DASHBOARD_TAB_CONFIRMING } from '../dashboard.types';
import { DashboardHelperService } from '../dashboard-helper.service';
import { CustomTabItem, TabAction } from '../../../shared-components/custom-tabs/custom-tabs.component';
import { SegmentedControlOption } from '../../../shared-components/custom-segmented-control/custom-segmented-control.component';

@Component({
	selector: 'app-dashboard-buyer',
	templateUrl: './dashboard-buyer.component.html',
	styleUrls: ['../private-home.component.scss'],
	standalone: false,
})
export class DashboardBuyerComponent implements OnInit {
	public loggedUser: User | null = new User();
	public requestErrors: OperationError[] = [];

	public CurrentMonth: Date;
	public documentForecast: DocumentForecast[] = [];
	public contractsCount: number = 0;
	public contractsAvailableAmount: number = 0;
	public contractedAmount: number = 0;
	public suppliersActiveConnections: number = 0;
	public contractsAvailableAmountRatio: number = 0;
	public notPaidDocuments: number = 0;
	public currentFundingRequestsAmount: number = 0;
	public confirmingPendingApprovalDocumentsAmount: number = 0;

	public documentForecastChart: VerticalBarChart = new VerticalBarChart();




	// Tab management
	public readonly activeTabId: string = DASHBOARD_TAB_CONFIRMING;
	public tabs: CustomTabItem[] = [];
	public tabActions: TabAction[] = [];

	public selectedCurrency: string = '';
	public orderedCurrencies: CurrencyViewModel[] = [];
	public currencyOptions: SegmentedControlOption[] = [];
	protected FileStatus = FileStatus;

	// Loading states for dashboard cards
	public loadingContractsAvailableAmount: boolean = false;
	public loadingContractedAmount: boolean = false;
	public loadingCurrentFundingRequestsAmount: boolean = false;
	public loadingNotPaidDocuments: boolean = false;
	public loadingSuppliersActiveConnections: boolean = false;
	public loadingConfirmingPendingApprovalDocumentsAmount: boolean = false;

	constructor(
		private reportingApiService: ReportingApiService,
		private serverApiService: ServerApiService,
		private appContext: AppContextService,
		private translateService: TranslateService,
		private toasterService: ToasterService,
		private dateLocale: DateLocalePipe,
		private router: Router,
		private fileIntegrationStateService: FileIntegrationStateService,
		private dashboardHelper: DashboardHelperService
	) {}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;

			this.getContracts();

			this.CurrentMonth = this.dateLocale.transform(new Date(), 'MM/yyyy');

			this.tabs = this.dashboardHelper.createTabs(true, false);
			this.tabActions = [this.dashboardHelper.createRefreshAction(() => this.refreshAllData())];

			this.documentForecastChart = this.dashboardHelper.createVerticalBarChart(
				this.translateService.translate('dashboard-buyer.chart.buyerConfirmedPaymentDate'),
				this.selectedCurrency
			);
		});
	}


	private getNotPaidDocumentsSum(currency: string): void {
		this.notPaidDocuments = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getNotPaidDocumentsSum(currency),
			(loading) => (this.loadingNotPaidDocuments = loading),
			(value) => (this.notPaidDocuments = value),
			'[DashboardBuyerComponent] Invalid result from getNotPaidDocumentsSum()'
		);
	}

	private getCurrentFundingRequestsAmount(currency: string): void {
		this.currentFundingRequestsAmount = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getConfirmingCurrentFundingRequestsAmount(currency),
			(loading) => (this.loadingCurrentFundingRequestsAmount = loading),
			(value) => (this.currentFundingRequestsAmount = value),
			'[DashboardBuyerComponent] Invalid result from getCurrentFundingRequestsAmount()'
		);
	}

	private getSuppliersActiveConnections(currency: string): void {
		this.suppliersActiveConnections = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getConfirmingSuppliersActiveConnections(currency),
			(loading) => (this.loadingSuppliersActiveConnections = loading),
			(value) => (this.suppliersActiveConnections = value),
			'[DashboardBuyerComponent] Invalid result from getSuppliersActiveConnections()'
		);
	}

	private getDocumentForecast(currency: string) {
		// Reset chart data before fetching new data
		this.documentForecastChart.Results = [];
		this.documentForecastChart.CustomColors = [];

		this.dashboardHelper.loadMetric(
			this.reportingApiService.getDailyDocumentForecast(currency),
			() => {},
			(data) => this.processDocumentForecast(data),
			'[DashboardBuyerComponent] Invalid result from getDocumentForecast',
			'[DashboardBuyerComponent] HttpError result from getDocumentForecast'
		);
	}

	private processDocumentForecast(data: DocumentForecastByState[]) {
		this.documentForecastChart.ShowLegend = data.some((d) => !!d.DocumentPaymentState);
		this.documentForecastChart.Results = this.dashboardHelper.buildDocumentForecastLineSeries(data, (d) =>
			this.dateLocale.transform(d)
		);
		this.documentForecastChart.CustomColors = this.dashboardHelper.buildDocumentForecastCustomColors(data);
	}

	private getPendingApprovalDocumentsAmount(currency: string) {
		this.confirmingPendingApprovalDocumentsAmount = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getPendingApprovalDocumentsAmount(currency),
			(loading) => (this.loadingConfirmingPendingApprovalDocumentsAmount = loading),
			(value) => (this.confirmingPendingApprovalDocumentsAmount = value.Confirming),
			'[DashboardBuyerComponent] Invalid result from getPendingApprovalDocumentsAmount',
			'[DashboardBuyerComponent] HttpError result from getPendingApprovalDocumentsAmount'
		);
	}

	private getContracts() {
		// Set loading states for values that are calculated in this method
		this.loadingContractedAmount = true;
		this.loadingContractsAvailableAmount = true;

		this.resetValues();

		let currencies: CurrencyViewModel[] = [];
		let retrievedConfirmingContracts: ConfirmingContract[] = [];

		const criteria: ConfirmingContractsSearchCriteria = new ConfirmingContractsSearchCriteria();
		criteria.OrderBy = 'EndDate';
		criteria.PageNumber = 1;
		criteria.OrderDescending = true;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new ConfirmingContractsSearchCriteria();
		criteria.RestrictionCriteria.ContractStateList = ['Active'];
		criteria.RestrictionCriteria.BuyerIdList = this.loggedUser?.Companies.map((c) => c.Id!) ?? [];

		this.serverApiService.searchConfirmingContracts(criteria).subscribe({
			next: (getConfirmingContracts: OperationResult<ConfirmingContract[]>) => {
				if (getConfirmingContracts.IsValid) {
					if (!!getConfirmingContracts.Data && getConfirmingContracts.Data.length > 0) {
						getConfirmingContracts.Data.forEach((contract) => {
							retrievedConfirmingContracts.push(contract);
							this.dashboardHelper.addCurrencyIfMissing(currencies, contract.Currency);
						});

						this.orderedCurrencies = this.dashboardHelper.sortCurrencies(currencies);
						this.currencyOptions = this.dashboardHelper.toCurrencyOptions(this.orderedCurrencies);

						if (!!this.orderedCurrencies && this.orderedCurrencies.length > 0) {
							let initialCurrencytoProcess = this.orderedCurrencies[0].Currency;

							let contractsOfInitialCurrency: ConfirmingContract[] = retrievedConfirmingContracts.filter(
								(c) => c.Currency == initialCurrencytoProcess
							);

							contractsOfInitialCurrency.forEach((contract) => {
								this.contractedAmount += contract.ContractedAmount;
								this.contractsAvailableAmount += contract.AvailableAmount;
								this.contractsAvailableAmountRatio = this.dashboardHelper.contractsAvailableRatio(
									this.contractsAvailableAmount,
									this.contractedAmount
								);
							});

							this.selectedCurrency = initialCurrencytoProcess;
							this.documentForecastChart.YAxisLabel = this.selectedCurrency;

							this.getNotPaidDocumentsSum(initialCurrencytoProcess);
							this.getDocumentForecast(initialCurrencytoProcess);
							this.getSuppliersActiveConnections(initialCurrencytoProcess);
							this.getCurrentFundingRequestsAmount(initialCurrencytoProcess);
							this.getPendingApprovalDocumentsAmount(initialCurrencytoProcess);
						}
					}
				} else {
					this.toasterService.showError(this.translateService.translate('ERROR_GET_COMPANY_CONTRACTS', 'list-errorCodes'));
				}

				// Set loading states to false after all calculations are complete
				this.loadingContractedAmount = false;
				this.loadingContractsAvailableAmount = false;
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	private refreshContractDataForCurrency(currency: string) {
		// Set loading states for values that are calculated in this method
		this.loadingContractedAmount = true;
		this.loadingContractsAvailableAmount = true;

		const criteria: ConfirmingContractsSearchCriteria = new ConfirmingContractsSearchCriteria();
		criteria.OrderBy = 'EndDate';
		criteria.PageNumber = 1;
		criteria.OrderDescending = true;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new ConfirmingContractsSearchCriteria();
		criteria.RestrictionCriteria.ContractStateList = ['Active'];
		criteria.RestrictionCriteria.BuyerIdList = this.loggedUser?.Companies.map((c) => c.Id!) ?? [];
		criteria.RestrictionCriteria.Currency = currency;

		this.serverApiService.searchConfirmingContracts(criteria).subscribe((getConfirmingContracts) => {
			if (!getConfirmingContracts.IsValid) {
				const errors = getConfirmingContracts.Errors;
				if (errors && errors.length > 0) {
					this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
						errors: errors,
						autoClose: false,
					});
				}
			} else {
				if (!!getConfirmingContracts.Data && getConfirmingContracts.Data.length > 0) {
					getConfirmingContracts.Data.forEach((contract) => {
						this.contractedAmount += contract.ContractedAmount;
						this.contractsAvailableAmount += contract.AvailableAmount;
						this.contractsAvailableAmountRatio = this.dashboardHelper.contractsAvailableRatio(
							this.contractsAvailableAmount,
							this.contractedAmount
						);
					});
				}
			}

			// Set loading states to false after all calculations are complete
			this.loadingContractedAmount = false;
			this.loadingContractsAvailableAmount = false;
		});
	}

	public clickedCurrency(currency: string): void {
		this.resetValues();

		this.selectedCurrency = currency;
		this.documentForecastChart.YAxisLabel = this.selectedCurrency;

		this.refreshContractDataForCurrency(this.selectedCurrency);
		this.getNotPaidDocumentsSum(this.selectedCurrency);
		this.getDocumentForecast(this.selectedCurrency);
		this.getSuppliersActiveConnections(this.selectedCurrency);
		this.getCurrentFundingRequestsAmount(this.selectedCurrency);
		this.getPendingApprovalDocumentsAmount(this.selectedCurrency);
	}

	private resetValues() {
		this.contractsAvailableAmount = 0;
		this.contractsAvailableAmountRatio = 0;
		this.contractedAmount = 0;
		this.currentFundingRequestsAmount = 0;
		this.notPaidDocuments = 0;
		this.suppliersActiveConnections = 0;
		this.confirmingPendingApprovalDocumentsAmount = 0;

		// Reset chart data
		this.documentForecastChart.Results = [];
		this.documentForecastChart.CustomColors = [];
	}

	public navigateToFilesIntegration(key: string): void {
		SearchFilesIntegrationComponent.navigateWithFileStatus(this.router, this.fileIntegrationStateService, key);
	}

	public refreshAllData(): void {
		this.getContracts();
	}
}
